/**
 * Country editor - fixes counterparties whose QBO country field is
 * blank. Detected order: QBO field → inferred from address text →
 * assumed UK. Staff overrides are stored in the app's own database
 * (QuickBooks stays read-only) and feed the country split charts.
 */
import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ChevronDown, ChevronRight, Search, Upload } from "lucide-react";
import { PanelTitle } from "../staff/CommissionedPanels";
import { Card } from "./cfoShared";
import { fmtGbp } from "./qboShared";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  "United Kingdom", "United States", "Ireland", "France", "Germany",
  "Switzerland", "Spain", "Italy", "Netherlands", "Belgium", "Denmark",
  "Sweden", "Norway", "Finland", "Austria", "Portugal", "Poland",
  "Canada", "Japan", "Australia", "Singapore", "India", "United Arab Emirates",
];

const SOURCE_BADGE = {
  override: { label: "Your override", cls: "bg-indigo-100 text-indigo-700" },
  registry: { label: "Client list", cls: "bg-violet-100 text-violet-700" },
  qbo: { label: "QuickBooks", cls: "bg-emerald-100 text-emerald-700" },
  inferred: { label: "From address", cls: "bg-sky-100 text-sky-700" },
  assumed: { label: "Assumed UK", cls: "bg-amber-100 text-amber-800" },
};

export default function CountryOverrides({ kind = "customer", noun = "client", onSaved, testid = "country-overrides" }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(40);
  const [otherFor, setOtherFor] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [registry, setRegistry] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const bounce = useRef(null);

  const loadRegistry = async () => {
    try {
      const { data } = await axios.get(`${API}/api/quickbooks/client-registry`);
      setRegistry(data);
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/quickbooks/counterparties/${kind}`);
      setRows(data.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load from QuickBooks - is it connected?");
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && rows == null && !loading) {
      load();
      if (kind === "customer") loadRegistry();
    }
  };

  const uploadRegistry = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/quickbooks/client-registry`, form);
      toast.success(`Imported ${data.imported} client numbers${data.skipped?.length ? ` (${data.skipped.length} rows skipped)` : ""}.`);
      loadRegistry();
      load();
      notifySaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import that file.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const notifySaved = () => {
    clearTimeout(bounce.current);
    bounce.current = setTimeout(() => onSaved?.(), 2000);
  };

  const save = async (row, country) => {
    const prev = rows;
    setRows((rs) =>
      rs.map((r) =>
        r.qb_id === row.qb_id
          ? {
              ...r,
              override: country || null,
              effective: country || r.detected,
              source: country ? "override" : r.detected_source,
              assumed: !country && r.detected_source === "assumed",
            }
          : r,
      ),
    );
    try {
      await axios.post(`${API}/api/quickbooks/country-override`, {
        kind, qb_id: row.qb_id, name: row.name, country: country || null,
      });
      toast.success(country ? `${row.name} → ${country}` : `Override cleared for ${row.name}`);
      notifySaved();
    } catch {
      setRows(prev);
      toast.error("Couldn't save the override - try again.");
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (!needle || r.name.toLowerCase().includes(needle)) &&
        (filter === "all" || r.source === filter),
    );
  }, [rows, q, filter]);

  const counts = useMemo(() => {
    if (!rows) return null;
    const by = { assumed: 0, inferred: 0, override: 0, registry: 0 };
    rows.forEach((r) => { if (by[r.source] != null) by[r.source] += 1; });
    return by;
  }, [rows]);

  return (
    <Card testid={testid}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid={`${testid}-toggle`}
      >
        <PanelTitle>
          <span className="inline-flex items-center gap-1.5">
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            Edit {noun} countries
          </span>
        </PanelTitle>
        <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400">
          stored in-app · QuickBooks untouched
        </span>
      </button>

      {open && (
        <div className="mt-3">
          {loading && <p className="text-xs text-stone-400">Loading every {noun} from QuickBooks…</p>}
          {error && <p className="text-xs text-rose-600">{error}</p>}
          {rows && (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setLimit(40); }}
                    placeholder={`Search ${noun}s…`}
                    className="pl-7 pr-2 py-1.5 text-xs border border-stone-200 rounded-md w-52 focus:outline-none focus:ring-1 focus:ring-[#1B4F8A]"
                    data-testid={`${testid}-search`}
                  />
                </div>
                {[
                  ["all", `All (${rows.length})`],
                  ["assumed", `Assumed UK (${counts.assumed})`],
                  ...(kind === "customer" ? [["registry", `Client list (${counts.registry})`]] : []),
                  ["inferred", `From address (${counts.inferred})`],
                  ["override", `Overridden (${counts.override})`],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setFilter(id); setLimit(40); }}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                      filter === id
                        ? "bg-[#1B4F8A] text-white border-[#1B4F8A]"
                        : "bg-white text-stone-600 border-stone-200 hover:border-stone-300"
                    }`}
                    data-testid={`${testid}-filter-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {kind === "customer" && (
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => uploadRegistry(e.target.files?.[0])}
                    data-testid={`${testid}-registry-file`}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md border border-stone-200 text-stone-600 hover:border-stone-300 disabled:opacity-50"
                    data-testid={`${testid}-registry-upload`}
                  >
                    <Upload className="w-3 h-3" />
                    {uploading ? "Importing…" : "Upload client-numbers list (.xlsx)"}
                  </button>
                  <span className="text-[10px] text-stone-400" data-testid={`${testid}-registry-status`}>
                    {registry?.count
                      ? `${registry.count} client numbers on file${registry.uploaded_at ? ` · updated ${new Date(registry.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""} - clients named 123A… get their country from it.`
                      : "No client-numbers list uploaded yet - upload it and clients named 123A… get their country automatically."}
                  </span>
                </div>
              )}
              <p className="text-[10px] text-stone-400 mb-2 leading-snug">
                Sorted by 12-month value - fix the biggest first. Pick a country to override;
                choose "Auto" to fall back to what was detected. Charts refresh a moment after you save.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-stone-100">
                      <th className="py-1.5 pr-3 font-medium">{noun}</th>
                      <th className="py-1.5 pr-3 font-medium text-right">12-mo net</th>
                      <th className="py-1.5 pr-3 font-medium">Detected</th>
                      <th className="py-1.5 font-medium">Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, limit).map((r) => {
                      const badge = SOURCE_BADGE[r.source] || SOURCE_BADGE.assumed;
                      const inList = !r.override || COUNTRIES.includes(r.override);
                      return (
                        <tr
                          key={r.qb_id}
                          className={`border-b border-stone-50 ${r.source === "assumed" ? "bg-amber-50/40" : ""}`}
                          data-testid={`${testid}-row-${r.qb_id}`}
                        >
                          <td className="py-1.5 pr-3 text-slate-800 max-w-[220px] truncate" title={r.name}>
                            {r.client_number && (
                              <span className="font-mono text-[10px] text-violet-500 mr-1.5">{r.client_number}</span>
                            )}
                            {r.name}
                          </td>
                          <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-stone-600 whitespace-nowrap">
                            {fmtGbp(r.net)}
                          </td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.cls}`}>
                              {badge.label}
                            </span>
                            <span className="ml-1.5 text-stone-500">{r.effective}</span>
                          </td>
                          <td className="py-1.5">
                            {otherFor === r.qb_id ? (
                              <span className="inline-flex items-center gap-1">
                                <input
                                  autoFocus
                                  value={otherText}
                                  onChange={(e) => setOtherText(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && otherText.trim()) { save(r, otherText.trim()); setOtherFor(null); setOtherText(""); }
                                    if (e.key === "Escape") { setOtherFor(null); setOtherText(""); }
                                  }}
                                  placeholder="Type a country…"
                                  className="px-2 py-1 text-xs border border-stone-300 rounded w-36 focus:outline-none focus:ring-1 focus:ring-[#1B4F8A]"
                                  data-testid={`${testid}-other-input-${r.qb_id}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => { if (otherText.trim()) { save(r, otherText.trim()); } setOtherFor(null); setOtherText(""); }}
                                  className="p-1 rounded bg-[#1B4F8A] text-white"
                                  data-testid={`${testid}-other-save-${r.qb_id}`}
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                              </span>
                            ) : (
                              <select
                                value={r.override || ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === "__other__") { setOtherFor(r.qb_id); setOtherText(""); return; }
                                  save(r, v || null);
                                }}
                                className="text-xs border border-stone-200 rounded-md px-1.5 py-1 bg-white max-w-[180px] focus:outline-none focus:ring-1 focus:ring-[#1B4F8A]"
                                data-testid={`${testid}-select-${r.qb_id}`}
                              >
                                <option value="">Auto - {r.detected}</option>
                                {!inList && <option value={r.override}>{r.override}</option>}
                                {COUNTRIES.map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                                <option value="__other__">Other…</option>
                              </select>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length > limit && (
                <button
                  type="button"
                  onClick={() => setLimit((l) => l + 100)}
                  className="mt-2 text-[11px] text-[#1B4F8A] hover:underline"
                  data-testid={`${testid}-show-more`}
                >
                  Show more ({filtered.length - limit} remaining)
                </button>
              )}
              {filtered.length === 0 && (
                <p className="text-xs text-stone-400 py-3">No {noun}s match.</p>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}
